'use client';

import React from 'react';
import { Star, Users, DollarSign, ArrowRight, Zap, Globe, Award, TrendingUp } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface Props {
  onNext: () => void;
}

const pillars = [
  {
    id: 'pillar-use',
    icon: Star,
    title: 'Use It',
    description:
      'Experience the product yourself. Your authentic results become your most powerful story. When you feel the difference, sharing becomes natural.',
    color: 'text-yellow-400',
    bg: 'rgba(234,179,8,0.08)',
    border: 'rgba(234,179,8,0.2)',
  },
  {
    id: 'pillar-share',
    icon: Users,
    title: 'Share It',
    description:
      'Earn when you share. Introduce X39 to friends, family, and your community — LifeWave pays you for every customer you bring in. No pressure, just genuine conversation that pays.',
    color: 'text-primary',
    bg: 'rgba(0,168,255,0.08)',
    border: 'rgba(0,168,255,0.2)',
  },
  {
    id: 'pillar-build',
    icon: DollarSign,
    title: 'Build It',
    description:
      'Build a team of brand partners and earn on their volume. Create a leveraged, residual income stream that grows as your network grows.',
    color: 'text-green-400',
    bg: 'rgba(74,222,128,0.08)',
    border: 'rgba(74,222,128,0.2)',
  },
];

const stats = [
  { id: 'stat-countries', icon: Globe, value: '100+', label: 'Countries' },
  { id: 'stat-partners', icon: Users, value: '150K+', label: 'Brand Partners' },
  { id: 'stat-founded', icon: Award, value: '2004', label: 'Founded' },
  { id: 'stat-patents', icon: Zap, value: '200+', label: 'Patents' },
];

export default function Slide6ABusiness({ onNext }: Props) {
  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center px-4 py-16 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 70% 40% at 50% 20%, rgba(0,168,255,0.08) 0%, transparent 70%)',
        }}
      />

      <div className="max-w-5xl w-full mx-auto fade-in">
        <div className="text-center mb-8">
          <p className="text-primary text-sm font-semibold tracking-widest uppercase mb-3">
            The Opportunity
          </p>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-foreground leading-tight mb-5">
            The <span className="gradient-text">LifeWave Opportunity</span>
          </h2>
          <p className="text-secondary-foreground text-lg max-w-2xl mx-auto leading-relaxed">
            Join thousands of Brand Partners worldwide who are building health, wealth, and
            freedom — all powered by a product that genuinely changes lives.
          </p>
        </div>

        {/* Explosive Growth Banner */}
        <div
          className="rounded-2xl p-6 mb-8 border text-center"
          style={{
            background: 'linear-gradient(135deg, rgba(74,222,128,0.08) 0%, rgba(0,168,255,0.10) 100%)',
            borderColor: 'rgba(74,222,128,0.35)',
            boxShadow: '0 0 30px rgba(74,222,128,0.1)',
          }}
        >
          <div className="flex items-center justify-center gap-3 mb-3">
            <TrendingUp size={28} className="text-green-400" />
            <span className="text-green-400 font-extrabold text-2xl sm:text-3xl">3,000%+ Growth</span>
            <TrendingUp size={28} className="text-green-400" />
          </div>
          <p className="text-foreground font-bold text-lg mb-1">
            Over 3,000% growth in the last six years.
          </p>
          <p className="text-secondary-foreground text-base">
            A technology that is changing <span className="text-primary font-semibold">thousands of lives</span> — and the momentum is just beginning.
          </p>
        </div>

        {/* Below wholesale highlight — moved to top */}
        <div
          className="glass-card p-6 mb-10 text-center border-primary/30"
          style={{ background: 'rgba(0,168,255,0.06)' }}
        >
          <p className="text-primary font-bold text-lg mb-1">
            Below-Wholesale Pricing — Exclusive to Brand Partners
          </p>
          <p className="text-secondary-foreground text-sm leading-relaxed max-w-xl mx-auto">
            When you enroll as a Brand Partner, you unlock pricing below the standard wholesale
            level — meaning you save more on every order AND earn on every sale you make.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.id} className="glass-card p-5 text-center">
                <Icon size={20} className="text-primary mx-auto mb-2" />
                <p className="gradient-text text-2xl font-extrabold tabular-nums">{stat.value}</p>
                <p className="text-muted-foreground text-xs mt-1">{stat.label}</p>
              </div>
            );
          })}
        </div>

        {/* Three Pillars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          {pillars.map((pillar, idx) => {
            const Icon = pillar.icon;
            return (
              <div
                key={pillar.id}
                className="glass-card p-7 text-center"
                style={{ background: pillar.bg, borderColor: pillar.border }}
              >
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: pillar.bg, border: `1px solid ${pillar.border}` }}
                >
                  <Icon size={30} className={pillar.color} />
                </div>
                <div
                  className="text-xs font-bold tracking-widest uppercase mb-1"
                  style={{ color: pillar.color.replace('text-', '') }}
                >
                  Step {idx + 1}
                </div>
                <h3 className="text-foreground font-bold text-xl mb-3">{pillar.title}</h3>
                <p className="text-secondary-foreground text-sm leading-relaxed">{pillar.description}</p>
              </div>
            );
          })}
        </div>

        {/* Income Disclaimer */}
        <p className="text-muted-foreground text-xs text-center mb-8 max-w-2xl mx-auto leading-relaxed">
          <em>Income Disclaimer: Results are not typical. There is no guarantee of income. Individual results will vary based on effort, experience, and market conditions. LifeWave Brand Partner earnings depend on personal sales and the sales of your team.</em>
        </p>

        <div className="text-center">
          <button onClick={onNext} className="btn-primary inline-flex items-center gap-2">
            See All Pricing &amp; Packages
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}